import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Connects to data-controller="self-check-in" on the attendee order/ticket page.
//
// Lets an attendee check themselves in (non-personalized events, self check-in
// enabled, window open — all enforced server-side). The window only opens 2h
// before the event, so the buttons are server-rendered and absent when closed;
// this controller just POSTs and reflects the result.
//
// Button actions:
//   - checkInAll  : batch — checks in all remaining tickets in the order.
//   - checkInOne  : one ticket on the order page (button carries data-slug).
//   - checkInTicket: the single-ticket page (/t/:slug), dedicated endpoint.
//
// On success we mark the matching ticket row(s) done and dispatch the same
// "ticket-check-in:toggled" event the organizer view uses, so the reused
// Entry Status summary (check-in-summary controller) updates itself.
export default class extends Controller {
  static targets = ["batchButton", "remainingBadge", "currentButton", "currentWrap", "currentHint"]
  static values = {
    orderUrl: String,   // POST endpoint for batch check-in (order page)
    ticketUrl: String,  // POST endpoint for the single-ticket page
  }

  connect() {
    // Slugs checked in during this page session — used to keep the inline
    // button hidden when the carousel returns to a just-checked-in ticket.
    this.checkedInSlugs = new Set()
    // The QR carousel announces which ticket is on screen; sync the inline
    // "Check In Now" button (below the QR) to that ticket.
    this._onCarousel = (e) => this.syncCurrent(e.detail)
    this.element.addEventListener("ticket-carousel:changed", this._onCarousel)

    // The carousel may have announced its initial slide before this listener
    // attached, so sync to the current active slide now too.
    const active = this.element.querySelector(".ticket-slide.active[data-slug]")
    if (active) this.syncCurrent({ slug: active.dataset.slug, checkedIn: active.dataset.checkedIn === "true" })
  }

  disconnect() {
    this.element.removeEventListener("ticket-carousel:changed", this._onCarousel)
  }

  // Point the inline button at the shown ticket; hide it (and the hint) once
  // that ticket is checked in.
  syncCurrent({ slug, checkedIn }) {
    if (!this.hasCurrentButtonTarget) return
    this.currentSlug = slug
    const done = checkedIn || this.checkedInSlugs.has(slug)
    if (this.hasCurrentWrapTarget) this.currentWrapTarget.hidden = done
  }

  // Check in the ticket currently shown in the carousel.
  checkInCurrent(event) {
    event.preventDefault()
    const slug = this.currentSlug || this.firstSlideSlug()
    if (!slug) return
    this.submit(this.orderUrlValue, [slug], event.currentTarget)
  }

  firstSlideSlug() {
    const slide = this.element.querySelector(".ticket-slide.active[data-slug]") ||
                  this.element.querySelector(".ticket-slide[data-slug]")
    return slide ? slide.dataset.slug : null
  }

  checkInAll(event) {
    event.preventDefault()
    // No slugs => server checks in every remaining ticket in the order.
    this.submit(this.orderUrlValue, null, event.currentTarget)
  }

  checkInTicket(event) {
    event.preventDefault()
    this.submit(this.ticketUrlValue, null, event.currentTarget)
  }

  // `slugs` is an array (scope check-in to those tickets) or null (whole order).
  async submit(url, slugs, button) {
    if (this.busy || !url) return
    this.busy = true
    button.classList.add("is-loading")
    button.disabled = true

    // Form-encode so Rails parses purchased_ticket_slugs[] into an array;
    // a JSON object body keeps the bracketed key literal and the scope is lost.
    let body
    if (slugs && slugs.length) {
      body = new FormData()
      slugs.forEach((s) => body.append("purchased_ticket_slugs[]", s))
    }

    try {
      const response = await post(url, body ? { body, responseKind: "json" } : { responseKind: "json" })
      const data = await response.json
      if (response.ok && data.success) {
        // Prefer the server's authoritative list; fall back to the requested
        // scope (e.g. single-ticket endpoint returns no list). checked_in_times
        // maps slug -> formatted timestamp for the status column.
        this.markCheckedIn(data.checked_in_slugs || slugs, button, data.checked_in_times || {})
        if (data.message) window.displayFlashMessage("success", data.message)
      } else {
        this.reenable(button)
        window.displayFlashMessage("error", (data && data.message) || "Check-in failed")
      }
    } catch (e) {
      this.reenable(button)
      window.displayFlashMessage("error", "Check-in failed")
    } finally {
      this.busy = false
    }
  }

  reenable(button) {
    button.classList.remove("is-loading")
    button.disabled = false
  }

  // Mark rows done. `slugs` null/empty => whole order (batch, no scope).
  // `times` maps slug -> formatted check-in timestamp for the status column.
  markCheckedIn(slugs, button, times = {}) {
    const matches = (el) => !slugs || slugs.length === 0 || slugs.includes(el.dataset.slug)

    const rows = this.element.querySelectorAll("[data-self-check-in-row]")
    rows.forEach((row) => {
      if (!matches(row) || row.classList.contains("self-check-in-row--done")) return

      row.classList.add("self-check-in-row--done")
      if (row.dataset.slug) this.checkedInSlugs.add(row.dataset.slug)

      // Status: "Ready" -> green "✓ Checked in" (the --done class adds the ✓).
      const status = row.querySelector("[data-self-check-in-row-status]")
      if (status) {
        status.textContent = status.dataset.checkedInLabel || "Checked in"
        status.classList.remove("self-check-in-status--ready")
        status.classList.add("self-check-in-status--done")
      }

      // Timestamp line below the status.
      const timeEl = row.querySelector("[data-self-check-in-row-time]")
      const stamp = times[row.dataset.slug]
      if (timeEl && stamp) {
        timeEl.textContent = stamp
        timeEl.hidden = false
      }

      const rowButton = row.querySelector("[data-self-check-in-row-button]")
      if (rowButton) rowButton.remove()

      // Drive the reused Entry Status summary, one tick per ticket.
      document.dispatchEvent(new CustomEvent("ticket-check-in:toggled", { detail: { checkedIn: true } }))
    })

    // Flip the matching QR carousel slides so the inline button hides when the
    // shown ticket is now checked in.
    this.element.querySelectorAll(".ticket-slide[data-slug]").forEach((slide) => {
      if (matches(slide)) {
        slide.dataset.checkedIn = "true"
        if (slide.dataset.slug) this.checkedInSlugs.add(slide.dataset.slug)
      }
    })
    // If the active slide is now done, hide the inline button immediately.
    const active = this.element.querySelector(".ticket-slide.active[data-slug]")
    if (active && this.checkedInSlugs.has(active.dataset.slug) && this.hasCurrentWrapTarget) {
      this.currentWrapTarget.hidden = true
    }

    // Re-enable the button: the inline button is a single element reused across
    // carousel slides, so it must stay usable for the next ticket. (The batch
    // button is removed once nothing remains; see updateBatchRemaining.)
    this.reenable(button)
    this.updateBatchRemaining()
  }

  // Keep the batch button's "remaining" badge in sync; remove the batch button
  // once nothing is left to check in.
  updateBatchRemaining() {
    const rows = this.element.querySelectorAll("[data-self-check-in-row]")
    const remaining = Array.from(rows).filter((r) => !r.classList.contains("self-check-in-row--done")).length
    if (this.hasRemainingBadgeTarget) this.remainingBadgeTarget.textContent = remaining
    if (this.hasBatchButtonTarget && remaining === 0) this.batchButtonTarget.remove()
  }
}
